import { theme } from 'antd';
import { createTheme } from '@mui/material/styles';

// Define color variables
const COLORS = {
  BACKGROUND: '#FFF',
  PRIMARY: '#1D2024',
  SECONDARY: '#6E7B8B',
  GREEN: '#62d8af',
  BLUE: '#009bff',
  RED: '#FF4136',
  GREY: '#eceeee',
  DARK_GREY: '#091E420F',
};

// Status Colors
const STATUS = {
  INFO: COLORS.BLUE,
  ERROR: COLORS.RED,
  SUCCESS: COLORS.GREEN,
};

const sharedTypography = {
  fontFamily: 'Inter, sans-serif',
  fontWeightRegular: 300,
  fontWeightBold: 400,
};


// Shared settings for both light and dark themes (not color-specific)
const sharedThemeConfig = {
  token: {
    fontFamily: 'Inter, sans-serif',
    fontWeightStrong: 500,   // Heading weight
    fontWeight: 300,         // Default text weight
    inputBorderColor: COLORS.BLUE, // Input border color
    inputHoverBorderColor: COLORS.BLUE,
    inputFocusBorderColor: COLORS.BLUE,
    colorBgMask: 'rgba(255, 255, 255, 0.2)', // Mask background color
  },
  components: {
    Avatar: {
      groupSpace: 20
    },
    Breadcrumb: {
      itemColor: "rgba(0, 0, 0, 0.75)",
      linkColor: "rgba(0, 0, 0, 0.75)",
      lastItemColor: "#000000",
    },
    Table: {
      bodySortBg: COLORS.GREY,
      borderColor: "#ededed",
      cellFontSize: 14,
      cellFontSizeMD: 12,
      cellFontSizeSM: 10,
      cellPaddingBlock: 10,
      cellPaddingBlockMD: 8,
      cellPaddingBlockSM: 6,
      headerBorderRadius: 4,
      filterDropdownBg: COLORS.GREY,
      filterDropdownMenuBg: COLORS.GREY,
      headerSplitColor: '#ededed',
      rowHoverBg: 'rgba(13, 42, 76, 0.09)',
      rowSelectedBg: COLORS.GREY,
      rowSelectedHoverBg: COLORS.GREY,
      stickyScrollBarBg: '#ededed',
      stickyScrollBarBorderRadius: '50%',
    },
    Layout: {
      triggerColor: '#fff',
      triggerBg: 'rgba(255, 255, 255, 0.12)',
      lightTriggerBg: '#444753',
    },
    Slider: {
      railBg: 'rgba(0, 0, 0, 0.1)',
      railSize: 3,
      dotSize: 12,
      handleSize: 12,
      trackBg: '#000',
      handleColor: '#000',
      dotBorderColor: '#000',
      dotActiveBorderColor: '#000',
      handleColorDisabled: COLORS.BLUE,
      handleActiveOutlineColor: '#000',
      handleActiveColor: '#000',
    },
    Input: {
      borderColor: COLORS.PRIMARY,
    },
    Menu: {
      itemSelectedBg: COLORS.DARK_GREY,
      subMenuItemBg: 'transparent',
      iconSize: 18,
      itemHeight: 40,
      iconMarginInlineEnd: 14,
    },
    Segmented: {
      trackPadding: 0,
      itemHoverBg: COLORS.DARK_GREY,
      itemSelectedBg: COLORS.DARK_GREY,
      itemColor: COLORS.SECONDARY,
      itemHoverColor: COLORS.PRIMARY,
      itemSelectedColor: COLORS.PRIMARY,
    },
    Progress: {
      circleTextFontSize: '0.8em',
    },
    Descriptions: {
      contentColor: COLORS.BLUE,
      labelBg: 'transparent',
      titleMarginBottom: 10,
      itemPaddingBottom: 0,
      itemPaddingEnd: 0,
    },
    Tabs: {
      cardPadding: '4px 16px 4px 16px',
      titleFontSize: 12,
      titleFontSizeLG: 14,
      titleFontSizeSM: 11,
      horizontalMargin: 0,
    },
    Typography: {
      titleMarginBottom: '0em',
      titleMarginTop: '0em',
    },
  },
};

// Light theme customization
export const customLightTheme = {
  ...sharedThemeConfig,
  algorithm: theme.defaultAlgorithm, // Light theme algorithm
  token: {
    ...sharedThemeConfig.token,
    colorBgBase: COLORS.BACKGROUND,
    colorBgPrimary: COLORS.BACKGROUND,
    colorBgLayout: '#fff',
    colorBorder: '#ededed',
    colorBorderSecondary: 'rgba(0,0,0,0.045)',
    colorPrimary: COLORS.PRIMARY,
    colorError: STATUS.ERROR,
    colorTextPrimary: '#FFFFFF',
    colorText: '#172B4D',
  },
  components: {
    ...sharedThemeConfig.components,
    Table: {
      ...sharedThemeConfig.components.Table,
      headerBg: COLORS.BACKGROUND,
      headerColor: COLORS.PRIMARY,
    },
  },
};

// Dark theme customization
export const customDarkTheme = {
  ...sharedThemeConfig,
  algorithm: theme.darkAlgorithm, // Dark theme algorithm
  token: {
    ...sharedThemeConfig.token,
    colorBgBase: COLORS.PRIMARY,
    colorBgPrimary: COLORS.PRIMARY,
    colorBgLayout: '#1A1A1A',
    colorBorder: '#333',
    colorPrimary: '#FFF',
    colorError: STATUS.ERROR,
  },
  components: {
    ...sharedThemeConfig.components,
    // Dark theme specific component overrides can be added here
  },
};

export const customLightMuiTheme = createTheme({
  palette: {
    primary: {
      main: COLORS.PRIMARY,
    },
    secondary: {
      main: COLORS.SECONDARY,
    },
    success: {
      main: STATUS.SUCCESS,
    },
    error: {
      main: STATUS.ERROR,
    },
    warning: {
      main: '#BF5700', // Warning color
    },
    background: {
      default: COLORS.BACKGROUND,
      paper: COLORS.BACKGROUND,
    },
    text: {
      primary: COLORS.PRIMARY,
      secondary: COLORS.SECONDARY,
    },
  },
  typography: {
    ...sharedTypography,
    h1: {
      color: COLORS.PRIMARY,
    },
    // Additional typography overrides if needed
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: ownerState.className?.includes('square') ? '4px' : '25px',
          fontWeight: sharedTypography.fontWeightBold,
          textTransform: 'capitalize',
          fontSize: '0.75rem',
          paddingTop: '6px', // Adjust this value to slightly reduce the top padding
          paddingBottom: '8px', // Optionally adjust bottom padding to keep balance
          ...(ownerState.color === 'info' && ownerState.variant === 'contained' && {
            backgroundColor: COLORS.BLUE,
          }),
          ...(ownerState.color === 'success' && {
            color: "#fff",
          }),
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        }),
        startIcon: {
          fontSize: '0.85rem',
        },
        endIcon: {
          fontSize: '0.85rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.BLUE,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.BLUE,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.BLUE,
          },
          ...(ownerState.size === 'small' && {
            padding: '1px',
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
            },
          }),
        }),
        input: {
          color: COLORS.PRIMARY,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        markLabel: {
          fontSize: '0.65rem',
          paddingTop: '2px',
          marginBottom: '2px',
          top: '22px',
          fontWeight: sharedTypography.fontWeightRegular,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.03)',
        },
        stickyHeader: {
          backgroundColor: COLORS.GREY,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'secondary' && ownerState.variant === 'outlined' && {
            border: 'none',
            background: 'linear-gradient(to right, #ff6e7f, #bfe9ff)',
          }),
          ...(ownerState.color === 'primary' && ownerState.variant === 'filled' && {
            padding: '16px 12px',
            borderRadius: 6,
            background: 'linear-gradient(to right, #161823, #0C0D14)',
          }),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === 'outlined' && {
            backgroundColor: '#fff',
          }),
        }),
      },
    },
  },
});

// Dark Theme for MUI
export const customDarkMuiTheme = createTheme({
  palette: {
    primary: {
      main: '#FFF', // Matches Ant Design dark theme primary
    },
    secondary: {
      main: COLORS.SECONDARY,
    },
    success: {
      main: STATUS.SUCCESS,
    },
    error: {
      main: STATUS.ERROR,
    },
    warning: {
      main: '#BF5700', // Warning color
    },
    background: {
      default: COLORS.PRIMARY,
      paper: '#1A1A1A',
    },
    text: {
      primary: '#FFF', // Lighter color for better contrast on dark background
      secondary: COLORS.SECONDARY,
    },
  },
  typography: {
    ...sharedTypography,
    h1: {
      color: '#FFF',
    },
    // Additional typography overrides if needed
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: ownerState.className?.includes('square') ? '4px' : '25px',
          fontWeight: sharedTypography.fontWeightBold,
          textTransform: 'capitalize',
          fontSize: '0.75rem',
          color: '#FFF',
          ...(ownerState.color === 'info' && ownerState.variant === 'contained' && {
            backgroundColor: COLORS.BLUE,
          }),
          ...(ownerState.color === 'success' && {
            color: "#FFF",
          }),
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFF',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.BLUE,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.BLUE,
          },
          ...(ownerState.size === 'small' && {
            padding: '1px',
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
            },
          }),
        }),
        input: {
          color: '#FFF',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        markLabel: {
          fontSize: '0.65rem',
          paddingTop: '2px',
          marginBottom: '2px',
          top: '22px',
          fontWeight: sharedTypography.fontWeightRegular,
          color: '#FFF',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #333',
        },
        stickyHeader: {
          backgroundColor: COLORS.PRIMARY,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'secondary' && ownerState.variant === 'outlined' && {
            border: 'none',
            background: 'linear-gradient(to right, #4e4e50, #161823)',
          }),
          ...(ownerState.color === 'primary' && ownerState.variant === 'filled' && {
            padding: '16px 12px',
            borderRadius: 6,
            background: 'linear-gradient(to right, #1A1A1A, #333)',
          }),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === 'outlined' && {
            backgroundColor: '#333',
            color: '#FFF',
          }),
        }),
      },
    },
  },
});